import { XMLSerializer } from 'xmldom';
import escapeHtml from 'escape-html';
import constants from '../../../Constants';
import xmlDomUtils from '../../../utils/XmlDomUtils';
import { generateFileUrlForLogicalName, parseImage } from '../MediaModels';
import { replacePrintedVariables } from '../itemBody';
import { t } from '../../../../services/i18nService';

const xmlSerializer = new XMLSerializer();

const interactionParserUtils = {
    getIdentifiersFromElements(elements) {
        const identifiers = [];
        for (const element of elements) {
            if (element.identifier) {
                identifiers.push(element.identifier);
            }
        }

        return identifiers;
    },

    /**
     * Gets the full URL for a media file.
     *
     * @param mediaObject
     * @param assessmentRunId - optional
     * @returns {string}
     */
    getMediaFileUrl(mediaObject, assessmentRunId) {
        const objectLogicalName = mediaObject['inspera:logicalName'];
        const mimeType = mediaObject.type;

        if (objectLogicalName) {
            return generateFileUrlForLogicalName(objectLogicalName, mimeType, assessmentRunId);
        }

        return undefined;
    },

    getBackgroundImageModel(interactionElement, assessmentRunId, mediaInfo) {
        const backgroundObject = Array
            .from(interactionElement.childNodes)
            .find(element => element.localName === 'object');

        if (!backgroundObject) {
            return undefined;
        }

        return parseImage('background', backgroundObject, assessmentRunId, mediaInfo);
    },

    /**
     * Returns the content in the provided language. If language is different from the defaultLanguage,
     * it will try to get the content in the other language from the inspera:label-lang- attribute
     * @param {Element} xmlElement
     * @param language
     * @param defaultLanguage
     * @returns {string}
     */
    getNodeContent(xmlElement, language, defaultLanguage) {
        if (!xmlElement) {
            return undefined;
        }

        if (language !== defaultLanguage
            && xmlElement.hasAttribute(constants.insperaLanguageAttrPrefix + language)
            // backwards compatibility for simpleChoice with image
            && !(xmlElement.nodeName === 'simpleChoice' && xmlElement.firstChild?.nodeName === 'img')
        ) {
            const xmlElementAttr = xmlElement.getAttribute(constants.insperaLanguageAttrPrefix + language);
            const firstChildElementNode = Array.from(xmlElement.childNodes).find(element => element.nodeType === Node.ELEMENT_NODE);
            if (xmlElement.nodeName === 'simpleChoice' && firstChildElementNode?.getAttribute('class').includes('Audio')) {
                // adding audio label translation (if exists) inside first <span class="Audio"></span>
                firstChildElementNode.textContent = xmlElementAttr;
            } else {
                return xmlElementAttr;
            }
        }

        let nodeString = '';
        for (const child of Array.from(xmlElement.childNodes)) {
            if (child.nodeName !== 'feedbackInline') {
                if (child.nodeType === Node.TEXT_NODE) {
                    nodeString += xmlSerializer.serializeToString(child.textContent);
                } else {
                    nodeString += xmlSerializer.serializeToString(child);
                }
            }
        }

        return nodeString.trim();
    },

    /**
     * Returns the content in the provided language. If language is different from the defaultLanguage,
     * it will try to get the content in the other language from the inspera:label-lang- attribute
     * @param node
     * @param language
     * @param defaultLanguage
     * @returns {string}
     */
    getNodeContentAsString(node, language, defaultLanguage) {
        const langAttributeName = `${constants.insperaLanguageAttrPrefix}${language}`;
        if (language !== defaultLanguage && node.hasAttribute(langAttributeName)) {
            return escapeHtml(node.getAttribute(langAttributeName));
        }

        const children = [];
        for (const child of Array.from(node.childNodes)) {
            if (child.nodeType !== 1 || language === defaultLanguage) {
                children.push(
                    xmlSerializer.serializeToString(child)
                );
            } else {
                const stringified = xmlSerializer.serializeToString(child);
                if (stringified.indexOf(langAttributeName) === 0) {
                    children.push(stringified);
                } else {
                    children.push(
                        this.getElementAsString(child, language, defaultLanguage)
                    );
                }
            }
        }

        return children.join('\n');
    },

    getElementAsString(node, language, defaultLanguage) {
        const attributes = Array.from(node.attributes)
            .map((attribute) => {
                return `${attribute.nodeName}="${escapeHtml(attribute.textContent)}"`;
            });

        let attributesString = attributes.join(' ');
        if (attributesString.length > 0) {
            attributesString = ` ${attributesString}`;
        }

        const content = this.getNodeContentAsString(node, language, defaultLanguage);
        return `<${node.tagName}${attributesString}>${content}</${node.tagName}>`;
    },

    getNonLanguageAttributes(element) {
        const attributes = {};
        for (const attribute of Array.from(element.attributes)) {
            if (attribute.nodeName.indexOf(constants.insperaLanguageAttrPrefix) === -1) {
                attributes[attribute.nodeName] = attribute.textContent;
            }
        }

        return attributes;
    },

    getObjectFromElement(element) {
        const obj = {};

        for (const attribute of Array.from(element.attributes)) {
            obj[attribute.nodeName] = attribute.textContent;
        }

        const elements = element.getElementsByTagName('*');
        for (const item of Array.from(elements)) {
            const tagNamePlural = `${item.tagName}s`;
            if (!obj[tagNamePlural]) {
                obj[tagNamePlural] = [];
            }

            obj[tagNamePlural].push(
                this.getObjectFromElement(item)
            );
        }

        obj.textContent = element.nodeValue;

        return obj;
    },

    getPrompt(rootElement, language, defaultLanguage) {
        const prompt = this.parseListOfChildren(rootElement, 'prompt', language, defaultLanguage);
        if (prompt.length) {
            const { content } = prompt[0];
            return {
                content,
                isRichText: prompt[0]['inspera:isRichText'] === 'true' || xmlDomUtils.isDomParsableString(content),
            };
        }
        return {
            content: '',
            isRichText: false,
        };
    },

    /**
     * Generic method to parse children (like simpleChoice and
     * @param rootElement
     * @param elementName
     * @param language
     * @param defaultLanguage
     * @returns {Array}
     */
    parseListOfChildren(rootElement, elementName, language, defaultLanguage) {
        const elements = rootElement.getElementsByTagName(elementName);
        const list = [];
        for (const element of Array.from(elements)) {
            const content = this.getNodeContent(element, language, defaultLanguage);
            const nonLanguageAttributes = this.getNonLanguageAttributes(element);

            list.push(
                Object.assign({ content }, nonLanguageAttributes)
            );
        }
        return list;
    },

    /**
     * @param objectElement
     * @param assessmentRunId
     */
    replaceObjectWithImg(objectElement, assessmentRunId) {
        const img = objectElement.ownerDocument.createElement('img');
        const attributes = xmlDomUtils.getAttributes(objectElement);
        const url = interactionParserUtils.getMediaFileUrl(attributes, assessmentRunId);

        img.setAttribute('src', url);
        img.setAttribute('id', attributes['inspera:logicalName']);
        img.setAttribute('width', attributes['inspera:orgWidth']);
        img.setAttribute('height', attributes['inspera:orgHeight']);

        objectElement.parentNode.replaceChild(img, objectElement);
    },

    /**
     * Returns the value transformed based on the baseType.
     * @param baseType
     * @param stringValue
     * @returns {string|number|array}
     */
    parseValueBasedOnBaseType(baseType, stringValue) {
        let value = null;
        if (baseType === 'boolean') {
            value = stringValue === 'true';
        } else if (baseType === 'integer') {
            value = parseInt(stringValue, 10);
        } else if (baseType === 'float') {
            value = parseFloat(stringValue);
        } else if (['string', 'duration', 'uri', 'identifier'].indexOf(baseType) > -1) {
            value = stringValue;
        } else if (baseType === 'point') {
            const parts = stringValue.split(' ');
            value = [parseInt(parts[0], 10), parseInt(parts[1], 10)];
        } else if (['pair', 'directedPair'].indexOf(baseType) > -1) {
            const parts = stringValue.split(' ');
            value = [parts[0], parts[1]];
        } else if (baseType === 'intOrIdentifier') {
            value = isNaN(stringValue) ? stringValue : parseInt(stringValue, 10);
        }
        // not supporting baseType=file yet.
        return value;
    },

    replaceVariablesAndSerializeFeedback(node, templateDeclarations, shouldReplaceVariables = false) {
        let nodeString = '';

        if (shouldReplaceVariables) {
            replacePrintedVariables(node, templateDeclarations, null, true);
        }

        for (const child of Array.from(node.childNodes)) {
            if (child.nodeName !== 'feedbackInline') {
                if (child.nodeType === Node.TEXT_NODE) {
                    nodeString += xmlSerializer.serializeToString(child.textContent);
                } else {
                    nodeString += xmlSerializer.serializeToString(child);
                }
            }
        }

        return nodeString.trim();
    },

    getLanguageNodeForFeedback(element, language, supportedLanguages) {
        if (!element || !language || !supportedLanguages) {
            return false;
        }

        let languageNode = element.childNodes.length &&
            Array.from(element.childNodes).find(node => (
                node.nodeType !== Node.TEXT_NODE &&
                    node.hasAttribute('lang') &&
                    node.getAttribute('lang') === language
            )
            );

        const isNewLanguageSupportFormat = this.isNewLanguageSupportFormat(element, supportedLanguages);

        if (isNewLanguageSupportFormat && !languageNode) {
            // if we do not have languageNode and support new language
            // we should get any child node from the element if it is exists
            // in this case we will take `en` translation
            if (element.childNodes.length) {
                languageNode = element.childNodes[0];
            } else {
                return false;
            }
        }

        return languageNode;
    },

    isNewLanguageSupportFormat(element, supportedLanguages) {
        if (
            !element ||
            !supportedLanguages ||
            !element.childNodes.length
        ) {
            return false;
        }

        let isNewLanguageSupportFormat = true;
        supportedLanguages.forEach((lang) => {
            if (element.hasAttribute(constants.insperaLanguageAttrPrefix + lang)) {
                isNewLanguageSupportFormat = false;
            }
        });

        if (!element.firstChild.tagName) {
            isNewLanguageSupportFormat = false;
        }

        return isNewLanguageSupportFormat;
    },

    prepareDynamicMathResponse(response) {
        if (!response) return '';
        const {
            ABSOLUTE, RELATIVE, GT, LT, NOT,
        } = constants.dynamicMathOperators;
        const { type, value } = response;

        if (type === ABSOLUTE) {
            const { lowerBound, upperBound, includeLowerBound, includeUpperBound } = response;
            const getNumberOfDecimals = stringValue => (`${stringValue}`.split('.')[1] || '').length;
            const calculateNumberOfDecimals = (boundNumberOfDecimals, valueNumberOfDecimals) =>
                (Math.max(boundNumberOfDecimals, valueNumberOfDecimals));
            const boundToFixed = (boundValue, numberOfDecimals) => Number(boundValue.toFixed(numberOfDecimals)).toString();
            const getBoundOperator = isBoundIncluded => (isBoundIncluded ? '≤' : '<');

            const valueNumberOfDecimals = getNumberOfDecimals(value);
            const lowerBoundNumberOfDecimals = getNumberOfDecimals(lowerBound);
            const upperBoundNumberOfDecimals = getNumberOfDecimals(upperBound);

            const numberOfDecimalsForLowerBoundValue = calculateNumberOfDecimals(lowerBoundNumberOfDecimals, valueNumberOfDecimals);
            const numberOfDecimalsForUpperBoundValue = calculateNumberOfDecimals(upperBoundNumberOfDecimals, valueNumberOfDecimals);

            const lowerBoundValue = boundToFixed(parseFloat(value) - parseFloat(lowerBound), numberOfDecimalsForLowerBoundValue);
            const upperBoundValue = boundToFixed(parseFloat(value) + parseFloat(upperBound), numberOfDecimalsForUpperBoundValue);

            return `${lowerBoundValue} ${getBoundOperator(includeLowerBound)} x ${getBoundOperator(includeUpperBound)} ${upperBoundValue}`;
        } else if (type === RELATIVE) {
            const { lowerBound, upperBound } = response;
            return t('dynamicMathResponse.output', { lowerBound, upperBound, value });
        } if (type === GT) {
            return `> ${value}`;
        } else if (type === LT) {
            return `< ${value}`;
        } else if (type === NOT) {
            return `≠ ${value}`;
        }

        return `${value}`;
    },
};

export default interactionParserUtils;
